import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import apiClient from '../networking/axiosconfig';


const ExcelUploader = () => {
  const { data: config, loading: configLoading, error: configError } = useSelector((state) => state.config);

  const modelName = 'products'; // Hardcoded model name
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [modelFields, setModelFields] = useState([]);
  const [mapping, setMapping] = useState({});
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [failedRecords, setFailedRecords] = useState([]);
  const [error, setError] = useState(null);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [singleUploadError, setSingleUploadError] = useState(null);

  const [filterMapping, setFilterMapping] = useState({});
  const [filterErrors, setFilterErrors] = useState([]);

  const productFields = [
    'name', 'brand', 'price', 'discountPercentage', 'description',
    'highlights', 'deliveryTime', 'isFeatured', 'colors', 'images', 'stock',
    'storeCode', 'specifications', 'content', 'isActive', 'sale', 'filters',
    'specificationKeys', 'specificationValues' // Virtual fields
  ];

  useEffect(() => {
    setModelFields(productFields);

    const fetchBrandsAndCategories = async () => {
      if (config?.API_BASE_URL) {
        setDataLoading(true);
        try {
          const brandsResponse = await apiClient.get(`${config.API_BASE_URL}/api/brands`);
          setBrands(brandsResponse.data.records || brandsResponse.data || []);
          const categoriesResponse = await apiClient.get(`${config.API_BASE_URL}/api/categories`);
          setCategories(categoriesResponse.data.records || categoriesResponse.data || []);
        } catch (err) {
          console.error("Failed to fetch brands or categories", err);
          setError("Failed to load initial data. Please check the console.");
        } finally {
          setDataLoading(false);
        }
      } else if (!configLoading) {
        setDataLoading(false);
      }
    };

    fetchBrandsAndCategories();
  }, [config, configLoading]);

  useEffect(() => {
    if (rows.length === 0 || !selectedCategory || Object.keys(filterMapping).length === 0) {
      setFilterErrors([]);
      return;
    }

    const errors = [];
    const category = selectedCategory.value;

    rows.forEach((row, rowIndex) => {
      for (const excelCol in filterMapping) {
        const filterKey = filterMapping[excelCol];
        if (!filterKey) continue;
        const filter = category.filters?.find(f => f.key === filterKey);

        if (filter) {
          const valueInExcel = row[excelCol];
          const isValidValue = filter.values?.some(v => v.value === valueInExcel);
          if (!isValidValue) {
            errors.push(`Row ${rowIndex + 2}: Invalid value "${valueInExcel}" for filter "${filterKey}".`);
          }
        }
      }
    });

    setFilterErrors(errors);
  }, [rows, filterMapping, selectedCategory]);

  if (configLoading || dataLoading) return <p>Loading data...</p>;
  if (configError) return <p>Error loading config: {configError}</p>;
  if (!config) return <p>Config not available</p>;

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = evt.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const sheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });
      const headers = sheet[0];
      const records = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

      setMapping({});
      setFilterMapping({});
      setColumns(headers);
      setRows(records);
      setShowPreview(false);
    };

    reader.readAsBinaryString(file);
  };

  const handleUnifiedMappingChange = (excelColumn, selectedOption) => {
    if (!selectedOption) {
      setMapping(prev => ({ ...prev, [excelColumn]: null }));
      setFilterMapping(prev => ({ ...prev, [excelColumn]: null }));
      return;
    }

    const { value, isFilter } = selectedOption;

    if (isFilter) {
      setFilterMapping(prev => ({ ...prev, [excelColumn]: value }));
      setMapping(prev => ({ ...prev, [excelColumn]: null }));
    } else {
      setMapping(prev => ({ ...prev, [excelColumn]: value }));
      setFilterMapping(prev => ({ ...prev, [excelColumn]: null }));
    }
  };

  const handlePreview = async () => {
    if (!selectedCategory) {
      alert("Please select a category for the upload.");
      return;
    }
    if (filterErrors.length > 0) {
      alert("Please fix the filter validation errors before proceeding.");
      return;
    }

    const brandNameToId = brands.reduce((acc, brand) => ({ ...acc, [brand.name]: brand._id }), {});

    const processedRows = rows.map(originalRow => {
      const payload = {
        name: '',
        brand: '',
        price: 0,
        discountPercentage: 0,
        description: '',
        highlights: '',
        deliveryTime: '',
        isFeatured: false,
        colors: [],
        images: [],
        stock: 0,
        storeCode: 'SONO55',
        specifications: {},
        content: '',
        filters: [],
        sale: null
      };

      for (const col in mapping) {
        if (originalRow[col] !== undefined && payload.hasOwnProperty(mapping[col])) {
          payload[mapping[col]] = originalRow[col];
        }
      }

      payload.category = selectedCategory.value._id;

      const specKeysCol = Object.keys(mapping).find(key => mapping[key] === 'specificationKeys');
      const specValuesCol = Object.keys(mapping).find(key => mapping[key] === 'specificationValues');
      if (specKeysCol && specValuesCol && originalRow[specKeysCol] && originalRow[specValuesCol]) {
        const keys = String(originalRow[specKeysCol]).split(',');
        const values = String(originalRow[specValuesCol]).split(',');
        if (keys.length === values.length) {
          payload.specifications = {};
          keys.forEach((key, index) => {
            payload.specifications[key.trim()] = values[index].trim();
          });
        }
      }

      const categoryForFilters = selectedCategory.value;
      if (categoryForFilters?.filters) {
        payload.filters = [];
        for (const excelCol in filterMapping) {
          const filterKey = filterMapping[excelCol];
          if (!filterKey) continue;
          const filter = categoryForFilters.filters.find(f => f.key === filterKey);
          if (filter && originalRow[excelCol] !== undefined) {
            const valueInExcel = originalRow[excelCol];
            const filterValue = filter.values?.find(v => v.value === valueInExcel);
            if (filterValue) {
              payload.filters.push({ key: filterKey, valueId: filterValue._id });
            }
          }
        }
      }

      if (payload.brand && brandNameToId[payload.brand]) {
        payload.brand = brandNameToId[payload.brand];
      }

      return payload;
    });

    setPreviewData(processedRows);
    setPreviewIndex(0);
    setProgress(0);
    setFailedRecords([]);
    setError(null);
    setSingleUploadError(null);
    setShowPreview(true);
  };

  const uploadSingleProduct = async () => {
    const record = previewData[previewIndex];
    if (!record) return;

    setUploading(true);
    setSingleUploadError(null);

    try {
      await apiClient.post(`${config.API_BASE_URL}/api/${modelName}`, record);
      
      setProgress(prev => prev + 1);
      
      if (previewIndex < previewData.length - 1) {
        setPreviewIndex(prev => prev + 1);
      } else {
        alert("All products have been processed!");
        setShowPreview(false);
      }

    } catch (err) {
      console.error(`Error uploading record ${previewIndex + 1}`, err);
      const errorMessage = err.response?.data?.message || err.message;
      setSingleUploadError(errorMessage);
      setFailedRecords(prev => [...prev, { ...record, uploadError: errorMessage }]);
    } finally {
      setUploading(false);
    }
  };
  
  const downloadFailed = () => {
    const ws = XLSX.utils.json_to_sheet(failedRecords);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'FailedRecords');
    XLSX.writeFile(wb, 'failed_records.xlsx');
  };

  return (
    <div style={{ display: 'flex', gap: '20px', padding: '20px' }}>
      <div style={{ flex: 1 }}>
        <h2>Product Excel Upload</h2>

        <div style={{ marginBottom: '20px' }}>
          <label>Select Category for Upload: </label>
          <Select
            options={categories.map(cat => ({ label: cat.name, value: cat }))}
            onChange={(cat) => {
              setSelectedCategory(cat);
              setShowPreview(false);
            }}
            value={selectedCategory}
            placeholder="Select a category..."
          />
        </div>

        <div style={{ marginTop: '20px' }}>
          <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} />
        </div>

        {columns.length > 0 && modelFields.length > 0 && selectedCategory && (() => {
          const fieldOptions = productFields.map(field => ({ label: field, value: field, isFilter: false }));
          const filterOptions = selectedCategory?.value?.filters?.map(f => ({ label: f.key, value: f.key, isFilter: true })) || [];
          const combinedOptions = [
            { label: 'Product Fields', options: fieldOptions },
            { label: 'Filters', options: filterOptions }
          ];
          const allOptions = [...fieldOptions, ...filterOptions];

          return (
            <>
              <table style={{ marginTop: '20px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '6px', border: '1px solid #ccc' }}>Excel Column</th>
                    <th style={{ padding: '6px', border: '1px solid #ccc' }}>Map To Field or Filter</th>
                  </tr>
                </thead>
                <tbody>
                  {columns.map((col, idx) => {
                    const selectedValue = mapping[col] || filterMapping[col];
                    const selectedOption = allOptions.find(opt => opt.value === selectedValue);
                    return (
                      <tr key={idx}>
                        <td style={{ padding: '6px', border: '1px solid #ccc' }}>{col}</td>
                        <td style={{ padding: '6px', border: '1px solid #ccc' }}>
                          <Select
                            options={combinedOptions}
                            onChange={(selected) => handleUnifiedMappingChange(col, selected)}
                            value={selectedOption || null}
                            isClearable
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <button onClick={handlePreview} disabled={uploading || filterErrors.length > 0} style={{ marginTop: '10px' }}>
                Generate Preview
              </button>
            </>
          );
        })()}

        {filterErrors.length > 0 && (
          <div style={{ marginTop: '20px', color: 'red' }}>
            <h4>Filter Validation Errors:</h4>
            <ul>
              {filterErrors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div style={{ flex: 1 }}>
        {showPreview && (
          <>
            <h2>Preview & Upload One-by-One</h2>
            <p><b>{progress} / {previewData.length}</b> products processed.</p>
            
            <h4>Payload for Product {previewIndex + 1}:</h4>
            {previewData[previewIndex] ? (
              <pre style={{ background: '#f4f4f4', border: '1px solid #ddd', padding: '10px', borderRadius: '5px', maxHeight: '300px', overflow: 'auto' }}>
                {JSON.stringify(previewData[previewIndex], null, 2)}
              </pre>
            ) : <p>No more products to upload.</p>}

            {singleUploadError && (
              <div style={{ marginTop: '10px', color: 'red' }}>
                <p><b>Upload Failed:</b> {singleUploadError}</p>
              </div>
            )}

            {previewIndex < previewData.length && (
              <button onClick={uploadSingleProduct} disabled={uploading} style={{ marginTop: '10px' }}>
                {uploading ? 'Uploading...' : `Upload This Product (${previewIndex + 1} of ${previewData.length})`}
              </button>
            )}

            {failedRecords.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                    <p>{failedRecords.length} record(s) failed to upload.</p>
                    <button onClick={downloadFailed}>Download Failed Records</button>
                </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExcelUploader;
